import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { IApplicationState } from "../../../store";
import * as Filestatus from "../../../store/filestatus";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as FileStatusData } from '../../../store/filestatus/filestatusList';
import { ICommonSelectControlsLinkedFromRedux } from "../forms/ICommonInputsControls";

// FileStatus interface from the store
type FileStatus = FileStatusData;

interface IFileStatus extends IOption {
    fileStatus: FileStatus;
}

const mapFileStatusToValue = (fileStatus: FileStatus): IFileStatus => {
    return {
        value: fileStatus.streamId,
        label: fileStatus.statusDutch,
        fileStatus: fileStatus
    }
}

// Props interface
interface IProps {
    selectedFileStatusId?: string;
}

// Actual Component
export type AllProps = IProps & ICommonSelectControlsLinkedFromRedux<FileStatus>;
export const FileStatusSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const filestatusState = useSelector((state: IApplicationState) => state.filestatus.filestatusList);
    const dispatch = useDispatch();

    // Internal state for selected file status - initialize with preselected file status if available
    const [selectedFileStatus, setSelectedFileStatus] = React.useState<IFileStatus | null>(() => {
        // Try to initialize with preselected file status if data is already available
        if (filestatusState && filestatusState.FilestatusList && filestatusState.FilestatusList.data && props.selectedFileStatusId) {
            const fileStatus = filestatusState.FilestatusList.data.find(fs => fs.streamId === props.selectedFileStatusId);
            if (fileStatus) {
                return mapFileStatusToValue(fileStatus);
            }
        }
        // If using value from common interface
        else if (props.value) {
            return mapFileStatusToValue(props.value);
        }
        return null;
    });

    // Fetch file statuses on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            !filestatusState || !filestatusState.FilestatusList ||
            filestatusState.FilestatusList.state === FetchState.NotFetched || 
            (filestatusState.FilestatusList.state === FetchState.Success && 
             (!filestatusState.FilestatusList.data || filestatusState.FilestatusList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(Filestatus.ActionCreators.filestatusList.FetchData());
        }
    }, [dispatch, filestatusState?.FilestatusList?.state, filestatusState?.FilestatusList?.data]);

    // Update selected file status when data is loaded or selectedFileStatusId prop changes
    React.useEffect(() => {
        if (filestatusState && filestatusState.FilestatusList && filestatusState.FilestatusList.data) {
            if (props.selectedFileStatusId) {
                const fileStatus = filestatusState.FilestatusList.data.find(fs => fs.streamId === props.selectedFileStatusId);
                if (fileStatus) {
                    const mappedFileStatus = mapFileStatusToValue(fileStatus);
                    setSelectedFileStatus(mappedFileStatus);
                } else {
                    setSelectedFileStatus(null);
                }
            } else if (props.value) {
                // Handle value from common interface
                setSelectedFileStatus(mapFileStatusToValue(props.value));
            } else {
                setSelectedFileStatus(null);
            }
        }
    }, [filestatusState?.FilestatusList?.data, props.selectedFileStatusId, props.value, filestatusState?.FilestatusList?.state]);

    // Map file statuses to select options
    const fileStatuses = React.useMemo(() => {
        return (filestatusState?.FilestatusList?.data || [])
            .map(mapFileStatusToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [filestatusState?.FilestatusList?.data, filestatusState?.FilestatusList?.state]);

    // Force component re-render when file statuses list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (filestatusState?.FilestatusList?.state === FetchState.Success && filestatusState?.FilestatusList?.data && filestatusState.FilestatusList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [filestatusState?.FilestatusList?.state, filestatusState?.FilestatusList?.data]);

    const updateFileStatus = (fileStatus: IFileStatus | null) => {
        setSelectedFileStatus(fileStatus);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            props.onChange(fileStatus?.fileStatus || null, !props.required || !!fileStatus);
        }
    }

    const isDisabled = props.disabled || !filestatusState?.FilestatusList || filestatusState.FilestatusList.state !== FetchState.Success;
    const isLoading = !filestatusState?.FilestatusList || filestatusState.FilestatusList.state === FetchState.Busy || filestatusState.FilestatusList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (filestatusState?.FilestatusList?.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<Select 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('statusLabel') || "Status") + (!filestatusState?.FilestatusList || filestatusState.FilestatusList.state !== FetchState.Success ? ` ${tCommon('loading')}` : "")}
        placeholder={props.placeholder || "Selecteer een status..."}
        options={fileStatuses}
        value={selectedFileStatus}
        disabled={isDisabled}
        onChange={updateFileStatus}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        icon={props.icon}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}
